import { createContext, forwardRef, useRef, useContext, useMemo, useState, useCallback, useId, useEffect } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { registerResponsiveContract, withConformance } from '@norkron/web-conformance';
import { Popover, PopoverTrigger, PopoverContent } from '@norkron/web-popover';
import { hslToHex, hexToRgb, hexToHsl, hexToOklch } from '@norkron/web-color';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { registerCodes } from '@norkron/web-errors';

// src/ColorPicker.tsx

// src/version.ts
var LIB_VERSION = "0.1.0";
var WEB_COLOR_PICKER_LIB = "@norkron/web-color-picker";
function clamp(n, lo, hi) {
  if (Number.isNaN(n)) return lo;
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}
function pad2(n) {
  const v = clamp(Math.round(n), 0, 255);
  return v.toString(16).padStart(2, "0");
}
function rgbToHex(r, g, b) {
  return `#${pad2(r)}${pad2(g)}${pad2(b)}`;
}
function rgbToHsv(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = (gn - bn) / d % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max * 100;
  const v = max * 100;
  return { h, s, v };
}
function hsvToRgb(h, s, v) {
  const hh = (h % 360 + 360) % 360;
  const sn = clamp(s, 0, 100) / 100;
  const vn = clamp(v, 0, 100) / 100;
  const c = vn * sn;
  const x = c * (1 - Math.abs(hh / 60 % 2 - 1));
  const m = vn - c;
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 60) {
    r1 = c;
    g1 = x;
  } else if (hh < 120) {
    r1 = x;
    g1 = c;
  } else if (hh < 180) {
    g1 = c;
    b1 = x;
  } else if (hh < 240) {
    g1 = x;
    b1 = c;
  } else if (hh < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255)
  };
}
function normaliseHex(input) {
  if (typeof input !== "string") return null;
  let raw = input.trim().toLowerCase();
  if (!raw.startsWith("#")) raw = `#${raw}`;
  const m = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/);
  if (!m) return null;
  const body = m[1];
  if (!body) return null;
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 1;
  if (body.length === 3) {
    r = parseInt(body[0] + body[0], 16);
    g = parseInt(body[1] + body[1], 16);
    b = parseInt(body[2] + body[2], 16);
  } else if (body.length === 4) {
    r = parseInt(body[0] + body[0], 16);
    g = parseInt(body[1] + body[1], 16);
    b = parseInt(body[2] + body[2], 16);
    a = parseInt(body[3] + body[3], 16) / 255;
  } else if (body.length === 6) {
    r = parseInt(body.slice(0, 2), 16);
    g = parseInt(body.slice(2, 4), 16);
    b = parseInt(body.slice(4, 6), 16);
  } else if (body.length === 8) {
    r = parseInt(body.slice(0, 2), 16);
    g = parseInt(body.slice(2, 4), 16);
    b = parseInt(body.slice(4, 6), 16);
    a = parseInt(body.slice(6, 8), 16) / 255;
  }
  return { hex: rgbToHex(r, g, b), alpha: a };
}
function parseRgbString(input) {
  const m = input.trim().toLowerCase().match(/^rgba?\(\s*([^,\s]+)[ ,]+([^,\s]+)[ ,]+([^,\s/]+)(?:\s*[/,]\s*([^)\s]+))?\s*\)$/);
  if (!m) return null;
  const toByte = (s) => {
    if (!s) return null;
    if (s.endsWith("%")) {
      const f = parseFloat(s.slice(0, -1));
      if (Number.isNaN(f)) return null;
      return clamp(Math.round(f / 100 * 255), 0, 255);
    }
    const n = parseFloat(s);
    if (Number.isNaN(n)) return null;
    return clamp(Math.round(n), 0, 255);
  };
  const toAlpha = (s) => {
    if (!s) return 1;
    if (s.endsWith("%")) {
      const f = parseFloat(s.slice(0, -1));
      return clamp(f / 100, 0, 1);
    }
    return clamp(parseFloat(s), 0, 1);
  };
  const r = toByte(m[1]);
  const g = toByte(m[2]);
  const b = toByte(m[3]);
  if (r === null || g === null || b === null) return null;
  return { hex: rgbToHex(r, g, b), alpha: toAlpha(m[4]) };
}
function parseHslString(input) {
  const m = input.trim().toLowerCase().match(/^hsla?\(\s*([^,\s]+)[ ,]+([^,\s]+)[ ,]+([^,\s/]+)(?:\s*[/,]\s*([^)\s]+))?\s*\)$/);
  if (!m) return null;
  const rawH = m[1];
  const hNum = parseFloat(rawH.replace(/deg$/, ""));
  if (Number.isNaN(hNum)) return null;
  const sStr = m[2];
  const lStr = m[3];
  const sNum = parseFloat(sStr);
  const lNum = parseFloat(lStr);
  if (Number.isNaN(sNum) || Number.isNaN(lNum)) return null;
  const hex = hslToHex((hNum % 360 + 360) % 360, clamp(sNum, 0, 100), clamp(lNum, 0, 100));
  let alpha = 1;
  if (m[4]) {
    alpha = m[4].endsWith("%") ? clamp(parseFloat(m[4].slice(0, -1)) / 100, 0, 1) : clamp(parseFloat(m[4]), 0, 1);
  }
  return { hex, alpha };
}
function parseAnyColor(input) {
  if (typeof input !== "string") return null;
  const t = input.trim().toLowerCase();
  if (t.startsWith("#") || /^[0-9a-f]{3,8}$/i.test(t)) return normaliseHex(t);
  if (t.startsWith("rgb")) return parseRgbString(t);
  if (t.startsWith("hsl")) return parseHslString(t);
  return null;
}
function formatColor(hex, alpha, format) {
  const [rr, gg, bb] = hexToRgb(hex);
  if (format === "hex") {
    if (alpha >= 1) return hex;
    const a = pad2(Math.round(alpha * 255));
    return `${hex}${a}`;
  }
  if (format === "rgb") {
    if (alpha >= 1) return `rgb(${rr}, ${gg}, ${bb})`;
    return `rgba(${rr}, ${gg}, ${bb}, ${alpha.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")})`;
  }
  if (format === "hsl") {
    const [hh, ss, ll] = hexToHsl(hex);
    const h = Math.round(hh);
    const s = Math.round(ss);
    const l = Math.round(ll);
    if (alpha >= 1) return `hsl(${h}, ${s}%, ${l}%)`;
    return `hsla(${h}, ${s}%, ${l}%, ${alpha.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")})`;
  }
  const [L, C, H] = hexToOklch(hex);
  const Hround = Number.isFinite(H) ? Math.round(H) : 0;
  if (alpha >= 1) return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${Hround})`;
  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${Hround} / ${alpha.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")})`;
}
function deriveChannels(hex, alpha) {
  const [r, g, b] = hexToRgb(hex);
  const [, hsls, hsll] = hexToHsl(hex);
  const hsv = rgbToHsv(r, g, b);
  return {
    r,
    g,
    b,
    h: hsv.h,
    s: hsls,
    l: hsll,
    v: hsv.v,
    sv: hsv.s,
    a: alpha
  };
}
function useColor(initial = "#000000") {
  const parsedInitial = useMemo(() => normaliseHex(initial) ?? { hex: "#000000", alpha: 1 }, [initial]);
  const [hex, setHex] = useState(parsedInitial.hex);
  const [alpha, setAlpha] = useState(parsedInitial.alpha);
  const set = useCallback((next) => {
    const parsed = parseAnyColor(next);
    if (!parsed) return;
    setHex(parsed.hex);
    setAlpha(parsed.alpha);
  }, []);
  const channels = useMemo(() => deriveChannels(hex, alpha), [hex, alpha]);
  const setChannel = useCallback(
    (channel, value) => {
      if (channel === "a") {
        setAlpha(clamp(value, 0, 1));
        return;
      }
      if (channel === "r" || channel === "g" || channel === "b") {
        const [r, g, b] = hexToRgb(hex);
        const cur = { r, g, b };
        const next = { ...cur, [channel]: clamp(Math.round(value), 0, 255) };
        setHex(rgbToHex(next.r, next.g, next.b));
        return;
      }
      if (channel === "h" || channel === "sv" || channel === "v") {
        const c = deriveChannels(hex, alpha);
        const h = channel === "h" ? (value % 360 + 360) % 360 : c.h;
        const sv = channel === "sv" ? clamp(value, 0, 100) : c.sv;
        const v = channel === "v" ? clamp(value, 0, 100) : c.v;
        const rgb = hsvToRgb(h, sv, v);
        setHex(rgbToHex(rgb.r, rgb.g, rgb.b));
        return;
      }
      if (channel === "s" || channel === "l") {
        const c = deriveChannels(hex, alpha);
        const h = c.h;
        const s = channel === "s" ? clamp(value, 0, 100) : c.s;
        const l = channel === "l" ? clamp(value, 0, 100) : c.l;
        setHex(hslToHex(h, s, l));
        return;
      }
    },
    [hex, alpha]
  );
  const formatted = useCallback((format) => formatColor(hex, alpha, format), [hex, alpha]);
  return {
    color: alpha >= 1 ? hex : formatColor(hex, alpha, "hex"),
    set,
    setChannel,
    channels,
    formatted
  };
}
function useColorPickerIds(base) {
  const auto = useId();
  const root = base ?? `web-color-picker-${auto}`;
  return {
    rootId: root,
    areaId: `${root}-area`,
    hueId: `${root}-hue`,
    alphaId: `${root}-alpha`
  };
}
function swatchStyle(hex) {
  return { "--swatch-color": hex };
}
function thumbStyle(mainAxisPct, orientation) {
  return orientation === "horizontal" ? { left: `${mainAxisPct}%`, top: "50%" } : { top: `${mainAxisPct}%`, left: "50%" };
}
var ColorPickerContext = createContext(null);
function useColorPickerContext(fn) {
  const ctx = useContext(ColorPickerContext);
  if (!ctx && typeof console !== "undefined") {
    console.warn(`[@norkron/web-color-picker] ${fn} rendered outside <ColorPicker>`);
  }
  return ctx;
}
function ColorPicker(props) {
  const {
    value: controlled,
    defaultValue = "#000000",
    format = "hex",
    onChange,
    disabled,
    id,
    children,
    className
  } = props;
  const ids = useColorPickerIds(id);
  const parsedInitial = useMemo(
    () => normaliseHex(defaultValue) ?? { hex: "#000000", alpha: 1 },
    [defaultValue]
  );
  const parsedControlled = useMemo(() => {
    if (controlled === void 0) return null;
    return parseAnyColor(controlled);
  }, [controlled]);
  const isControlled = parsedControlled !== null;
  const [uncontrolledHex, setUncontrolledHex] = useState(parsedInitial.hex);
  const [uncontrolledAlpha, setUncontrolledAlpha] = useState(parsedInitial.alpha);
  const hex = isControlled ? parsedControlled.hex : uncontrolledHex;
  const alpha = isControlled ? parsedControlled.alpha : uncontrolledAlpha;
  const channels = useMemo(() => deriveChannels(hex, alpha), [hex, alpha]);
  const emitRef = useRef(
    { hex, alpha, format, onChange }
  );
  emitRef.current = { hex, alpha, format, onChange };
  const emit = useCallback((nextHex, nextAlpha) => {
    const h = nextHex ?? emitRef.current.hex;
    const a = nextAlpha ?? emitRef.current.alpha;
    emitRef.current.onChange?.(formatColor(h, a, emitRef.current.format));
  }, []);
  const setHex = useCallback(
    (next) => {
      if (disabled) return;
      if (!isControlled) setUncontrolledHex(next);
      emitRef.current = { ...emitRef.current, hex: next };
      emit(next, emitRef.current.alpha);
    },
    [disabled, isControlled, emit]
  );
  const setAlpha = useCallback(
    (next) => {
      if (disabled) return;
      const a = clamp(next, 0, 1);
      if (!isControlled) setUncontrolledAlpha(a);
      emitRef.current = { ...emitRef.current, alpha: a };
      emit(emitRef.current.hex, a);
    },
    [disabled, isControlled, emit]
  );
  const setChannel = useCallback(
    (channel, value) => {
      if (disabled) return;
      if (channel === "a") {
        setAlpha(value);
        return;
      }
      if (channel === "r" || channel === "g" || channel === "b") {
        const next = { r: channels.r, g: channels.g, b: channels.b, [channel]: clamp(Math.round(value), 0, 255) };
        setHex(rgbToHex(next.r, next.g, next.b));
        return;
      }
      if (channel === "h" || channel === "sv" || channel === "v") {
        const h = channel === "h" ? (value % 360 + 360) % 360 : channels.h;
        const sv2 = channel === "sv" ? clamp(value, 0, 100) : channels.sv;
        const v2 = channel === "v" ? clamp(value, 0, 100) : channels.v;
        const rgb2 = hsvToRgb(h, sv2, v2);
        setHex(rgbToHex(rgb2.r, rgb2.g, rgb2.b));
        return;
      }
      const cur = channels;
      const newL = channel === "l" ? clamp(value, 0, 100) : cur.l;
      const newS = channel === "s" ? clamp(value, 0, 100) : cur.s;
      const v = Math.min(100, newL + newS * Math.min(newL, 100 - newL) / 100);
      const sv = v === 0 ? 0 : 2 * (1 - newL / v) * 100;
      const rgb = hsvToRgb(cur.h, clamp(sv, 0, 100), v);
      setHex(rgbToHex(rgb.r, rgb.g, rgb.b));
    },
    [channels, disabled, setAlpha, setHex]
  );
  const ctxValue = {
    hex,
    alpha,
    format,
    channels,
    rootId: ids.rootId,
    areaId: ids.areaId,
    hueId: ids.hueId,
    alphaId: ids.alphaId,
    setHex,
    setAlpha,
    setChannel,
    emit
  };
  const cls = ["color-picker", disabled ? "color-picker--disabled" : null, className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(ColorPickerContext.Provider, { value: ctxValue, children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }), children: /* @__PURE__ */ jsx(
    "div",
    {
      className: cls,
      id: ids.rootId,
      "data-color": hex,
      "data-alpha": alpha.toFixed(3),
      role: "group",
      "aria-label": "Color picker",
      ...withConformance(WEB_COLOR_PICKER_LIB, LIB_VERSION),
      children
    }
  ) }) });
}
function ColorSwatchGrid(props) {
  const ctx = useColorPickerContext("ColorSwatchGrid");
  const cls = ["color-picker__swatch-grid", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { role: "listbox", "aria-label": props.ariaLabel ?? "Preset colors", className: cls, children: props.colors.map((c, idx) => {
    const normalised = normaliseHex(c);
    const hex = normalised?.hex ?? "#000000";
    const isSelected = ctx?.hex === hex;
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        role: "option",
        "aria-selected": isSelected,
        "aria-label": hex,
        className: [
          "color-picker__swatch",
          isSelected ? "color-picker__swatch--selected" : null
        ].filter(Boolean).join(" "),
        "data-color": hex,
        style: swatchStyle(hex),
        onClick: () => {
          ctx?.setHex(hex);
          props.onSelect?.(hex);
          ctx?.emit();
        }
      },
      `${hex}-${idx}`
    );
  }) });
}
var ColorArea = forwardRef(function ColorArea2(props, ref) {
  const ctx = useColorPickerContext("ColorArea");
  const localRef = useRef(null);
  const draggingRef = useRef(null);
  const setRef = (n) => {
    localRef.current = n;
    if (typeof ref === "function") ref(n);
    else if (ref) ref.current = n;
  };
  const handleAt = (clientX, clientY) => {
    const node = localRef.current;
    if (!node || !ctx) return;
    const r = node.getBoundingClientRect();
    const width = r.width || 1;
    const height = r.height || 1;
    const sv2 = clamp((clientX - r.left) / width * 100, 0, 100);
    const v2 = clamp(100 - (clientY - r.top) / height * 100, 0, 100);
    const rgb = hsvToRgb(ctx.channels.h, sv2, v2);
    ctx.setHex(rgbToHex(rgb.r, rgb.g, rgb.b));
  };
  const onPointerDown = (e) => {
    if (!ctx) return;
    draggingRef.current = e.pointerId;
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch {
    }
    handleAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    handleAt(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    draggingRef.current = null;
    ctx?.emit();
  };
  const onKeyDown = (e) => {
    if (!ctx) return;
    const step = e.shiftKey ? 10 : 1;
    let { sv: sv2, v: v2 } = ctx.channels;
    switch (e.key) {
      case "ArrowLeft":
        sv2 = clamp(sv2 - step, 0, 100);
        break;
      case "ArrowRight":
        sv2 = clamp(sv2 + step, 0, 100);
        break;
      case "ArrowUp":
        v2 = clamp(v2 + step, 0, 100);
        break;
      case "ArrowDown":
        v2 = clamp(v2 - step, 0, 100);
        break;
      default:
        return;
    }
    e.preventDefault();
    const rgb = hsvToRgb(ctx.channels.h, sv2, v2);
    ctx.setHex(rgbToHex(rgb.r, rgb.g, rgb.b));
    ctx.emit();
  };
  const sv = ctx?.channels.sv ?? 0;
  const v = ctx?.channels.v ?? 0;
  const cls = ["color-picker__area", props.className].filter(Boolean).join(" ");
  const hue = Math.round(ctx?.channels.h ?? 0);
  const x = sv.toFixed(2);
  const y = v.toFixed(2);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setRef,
      id: ctx?.areaId,
      role: "slider",
      "aria-label": props.ariaLabel ?? "Saturation and value",
      "aria-valuetext": `saturation ${Math.round(sv)}, value ${Math.round(v)}`,
      tabIndex: 0,
      className: cls,
      "data-hue": hue,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      style: { ...props.style, "--area-hue": `hsl(${hue}, 100%, 50%)` },
      children: [
        /* @__PURE__ */ jsx("div", { className: "color-picker__area-saturation" }),
        /* @__PURE__ */ jsx("div", { className: "color-picker__area-value" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "color-picker__cursor",
            "data-x": x,
            "data-y": y,
            style: { left: `${x}%`, top: `${(100 - Number(y)).toFixed(2)}%` },
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
});
function ColorHueSlider(props) {
  const ctx = useColorPickerContext("ColorHueSlider");
  const orientation = props.orientation ?? "horizontal";
  const localRef = useRef(null);
  const draggingRef = useRef(null);
  const handleAt = (clientX, clientY) => {
    const node = localRef.current;
    if (!node || !ctx) return;
    const r = node.getBoundingClientRect();
    const ratio = orientation === "horizontal" ? clamp((clientX - r.left) / (r.width || 1), 0, 1) : clamp((clientY - r.top) / (r.height || 1), 0, 1);
    ctx.setChannel("h", ratio * 360);
  };
  const onPointerDown = (e) => {
    if (!ctx) return;
    draggingRef.current = e.pointerId;
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch {
    }
    handleAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    handleAt(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    draggingRef.current = null;
    ctx?.emit();
  };
  const onKeyDown = (e) => {
    if (!ctx) return;
    const step = e.shiftKey ? 10 : 1;
    let h = ctx.channels.h;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        h -= step;
        break;
      case "ArrowRight":
      case "ArrowUp":
        h += step;
        break;
      case "Home":
        h = 0;
        break;
      case "End":
        h = 359;
        break;
      default:
        return;
    }
    e.preventDefault();
    ctx.setChannel("h", (h % 360 + 360) % 360);
    ctx.emit();
  };
  const hue = Math.round(ctx?.channels.h ?? 0);
  const cls = [
    "color-picker__hue-slider",
    `color-picker__hue-slider--${orientation}`,
    props.className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: localRef,
      id: ctx?.hueId,
      role: "slider",
      "aria-label": props.ariaLabel ?? "Hue",
      "aria-orientation": orientation,
      "aria-valuemin": 0,
      "aria-valuemax": 360,
      "aria-valuenow": hue,
      tabIndex: 0,
      className: cls,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      children: [
        /* @__PURE__ */ jsx("div", { className: "color-picker__hue-track" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "color-picker__hue-thumb",
            "data-hue": hue,
            style: thumbStyle((hue / 360 * 100).toFixed(2), orientation),
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function ColorAlphaSlider(props) {
  const ctx = useColorPickerContext("ColorAlphaSlider");
  const orientation = props.orientation ?? "horizontal";
  const localRef = useRef(null);
  const draggingRef = useRef(null);
  const handleAt = (clientX, clientY) => {
    const node = localRef.current;
    if (!node || !ctx) return;
    const r = node.getBoundingClientRect();
    const ratio = orientation === "horizontal" ? clamp((clientX - r.left) / (r.width || 1), 0, 1) : clamp((clientY - r.top) / (r.height || 1), 0, 1);
    ctx.setAlpha(ratio);
  };
  const onPointerDown = (e) => {
    if (!ctx) return;
    draggingRef.current = e.pointerId;
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch {
    }
    handleAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    handleAt(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (draggingRef.current !== e.pointerId) return;
    draggingRef.current = null;
    ctx?.emit();
  };
  const onKeyDown = (e) => {
    if (!ctx) return;
    const stepPct = e.shiftKey ? 0.1 : 0.01;
    let a = ctx.alpha;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        a -= stepPct;
        break;
      case "ArrowRight":
      case "ArrowUp":
        a += stepPct;
        break;
      case "Home":
        a = 0;
        break;
      case "End":
        a = 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    ctx.setAlpha(a);
    ctx.emit();
  };
  const alphaValue = ctx?.alpha ?? 1;
  const cls = [
    "color-picker__alpha-slider",
    `color-picker__alpha-slider--${orientation}`,
    props.className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: localRef,
      id: ctx?.alphaId,
      role: "slider",
      "aria-label": props.ariaLabel ?? "Alpha",
      "aria-orientation": orientation,
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": Math.round((ctx?.alpha ?? 1) * 100),
      tabIndex: 0,
      className: cls,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      children: [
        /* @__PURE__ */ jsx("div", { className: "color-picker__alpha-track" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "color-picker__alpha-thumb",
            "data-alpha": alphaValue.toFixed(3),
            style: thumbStyle((alphaValue * 100).toFixed(2), orientation),
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function channelRange(channel) {
  switch (channel) {
    case "r":
    case "g":
    case "b":
      return { min: 0, max: 255, step: 1 };
    case "h":
      return { min: 0, max: 360, step: 1 };
    case "s":
    case "l":
      return { min: 0, max: 100, step: 1 };
    case "a":
      return { min: 0, max: 1, step: 0.01 };
  }
}
function readChannel(channels, channel) {
  switch (channel) {
    case "r":
      return channels.r;
    case "g":
      return channels.g;
    case "b":
      return channels.b;
    case "h":
      return channels.h;
    case "s":
      return channels.s;
    case "l":
      return channels.l;
    case "a":
      return channels.a;
  }
}
function ColorChannelInput(props) {
  const ctx = useColorPickerContext("ColorChannelInput");
  const range = channelRange(props.channel);
  const raw = readChannel(ctx?.channels ?? deriveChannels("#000000", 1), props.channel);
  const display = props.channel === "a" ? raw.toFixed(2) : String(Math.round(raw));
  const cls = [
    "color-picker__channel-input",
    `color-picker__channel-input--${props.channel}`,
    props.className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "input",
    {
      type: "number",
      className: cls,
      "aria-label": props.ariaLabel ?? `Channel ${props.channel}`,
      min: range.min,
      max: range.max,
      step: range.step,
      value: display,
      "data-channel": props.channel,
      onChange: (e) => {
        const n = parseFloat(e.target.value);
        if (Number.isNaN(n)) return;
        ctx?.setChannel(props.channel, n);
        ctx?.emit();
      }
    }
  );
}
function ColorTextInput(props) {
  const ctx = useColorPickerContext("ColorTextInput");
  const format = props.format ?? "hex";
  const canonical = ctx ? formatColor(ctx.hex, ctx.alpha, format) : "";
  const [draft, setDraft] = useState(canonical);
  const [committedSig, setCommittedSig] = useState(canonical);
  useEffect(() => {
    if (canonical !== committedSig) {
      setDraft(canonical);
      setCommittedSig(canonical);
    }
  }, [canonical]);
  const cls = [
    "color-picker__text-input",
    `color-picker__text-input--${format}`,
    props.className
  ].filter(Boolean).join(" ");
  const commit = (next) => {
    const parsed = parseAnyColor(next);
    if (!parsed) {
      setDraft(canonical);
      return;
    }
    ctx?.setHex(parsed.hex);
    ctx?.setAlpha(parsed.alpha);
    ctx?.emit();
  };
  return /* @__PURE__ */ jsx(
    "input",
    {
      type: "text",
      spellCheck: false,
      autoComplete: "off",
      className: cls,
      "aria-label": props.ariaLabel ?? `Color ${format}`,
      "data-format": format,
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      onBlur: (e) => commit(e.target.value),
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit(draft);
        } else if (e.key === "Escape") {
          setDraft(canonical);
        }
      }
    }
  );
}
function ColorEyeDropper(props) {
  const ctx = useColorPickerContext("ColorEyeDropper");
  const supported = typeof window !== "undefined" && typeof window.EyeDropper === "function";
  if (!supported) return null;
  const cls = ["color-picker__eyedropper", props.className].filter(Boolean).join(" ");
  const onClick = async () => {
    try {
      const Ctor = window.EyeDropper;
      const inst = new Ctor();
      const res = await inst.open();
      const parsed = normaliseHex(res.sRGBHex);
      if (parsed) {
        ctx?.setHex(parsed.hex);
        ctx?.emit();
      }
    } catch {
    }
  };
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: cls,
      "aria-label": props.ariaLabel ?? "Pick color from screen",
      "data-eyedropper-supported": "true",
      onClick,
      children: props.children ?? "Pick"
    }
  );
}
function ColorPickerPopover(props) {
  const {
    value,
    defaultValue = "#000000",
    format = "hex",
    onChange,
    presets,
    disabled,
    renderTrigger,
    children,
    className
  } = props;
  const display = value ?? defaultValue;
  const cls = ["color-picker__popover", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: renderTrigger ? renderTrigger(display) : /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "color-picker__popover-trigger",
        "aria-label": `Current color ${display}`,
        "data-color": display,
        disabled,
        children: /* @__PURE__ */ jsx(
          "span",
          {
            className: "color-picker__popover-swatch",
            "aria-hidden": "true",
            "data-color": display,
            style: swatchStyle(display)
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: cls, children: /* @__PURE__ */ jsx(
      ColorPicker,
      {
        value,
        defaultValue,
        format,
        onChange,
        presets,
        disabled,
        children: children ?? /* @__PURE__ */ jsxs(Fragment, { children: [
          presets && presets.length > 0 ? /* @__PURE__ */ jsx(ColorSwatchGrid, { colors: presets }) : null,
          /* @__PURE__ */ jsx(ColorArea, {}),
          /* @__PURE__ */ jsx(ColorHueSlider, {}),
          /* @__PURE__ */ jsx(ColorAlphaSlider, {}),
          /* @__PURE__ */ jsx(ColorTextInput, { format })
        ] })
      }
    ) })
  ] });
}
function ColorPickerInput(props) {
  const { inputAriaLabel, ...rest } = props;
  const cls = ["color-picker__input", props.className].filter(Boolean).join(" ");
  const display = props.value ?? props.defaultValue ?? "#000000";
  return /* @__PURE__ */ jsx("div", { className: cls, children: /* @__PURE__ */ jsx(
    ColorPickerPopover,
    {
      ...rest,
      renderTrigger: (c) => /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: "color-picker__input-trigger",
          "aria-label": inputAriaLabel ?? `Pick color (current ${c})`,
          "data-color": c,
          children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: "color-picker__input-swatch",
                "data-color": c,
                "aria-hidden": "true",
                style: swatchStyle(c)
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "color-picker__input-value", children: display })
          ]
        }
      )
    }
  ) });
}
var LIB_ID = "@norkron/web-color-picker";
try {
  registerCodes(LIB_ID, {
    WEB_COLOR_PICKER_SWATCH_MISSING_ARIA_SELECTED: {
      description: 'selfCheck finding: a .color-picker__swatch element lives inside a [role="listbox"] but lacks the aria-selected attribute. Assistive tech cannot announce the active swatch.',
      severity: "warning",
      docsAnchor: "web-color-picker/swatch-missing-aria-selected",
      exampleFix: "Set aria-selected on every .color-picker__swatch (true on the active one, false on the rest)."
    },
    WEB_COLOR_PICKER_FORMAT_PARSE_ROUNDTRIP_BROKEN: {
      description: "selfCheck finding: the root color-picker `data-color` value, when parsed and re-emitted via the lib formatters, differs from the source beyond the 1-bit channel threshold. The format pipeline is losing precision.",
      severity: "error",
      docsAnchor: "web-color-picker/format-roundtrip-broken",
      exampleFix: "Audit the parse \u2192 derive \u2192 format chain for the affected format; ensure HSV \u2194 RGB conversions clamp + round identically on both legs."
    },
    WEB_COLOR_PICKER_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck info finding: the self-check threw internally and was caught.",
      severity: "info",
      docsAnchor: "web-color-picker/selfcheck-internal-error"
    }
  });
} catch {
}
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function normaliseHexLocal(input) {
  let s = input.trim().toLowerCase();
  if (!s.startsWith("#")) return null;
  s = s.slice(1);
  if (s.length === 3) {
    s = s.split("").map((c) => c + c).join("");
  }
  if (s.length === 6 || s.length === 8) {
    if (!/^[0-9a-f]+$/.test(s)) return null;
    return `#${s.slice(0, 6)}`;
  }
  return null;
}
function hexToRgb2(hex) {
  const n = normaliseHexLocal(hex);
  if (!n) return null;
  return [
    Number.parseInt(n.slice(1, 3), 16),
    Number.parseInt(n.slice(3, 5), 16),
    Number.parseInt(n.slice(5, 7), 16)
  ];
}
function selfCheck(root, opts) {
  const safeMin = opts?.minSeverity ?? "info";
  const findings = [];
  try {
    if (!root || typeof root.querySelectorAll !== "function") {
      return findings.filter((f) => meetsFloor(f, safeMin));
    }
    const listboxes = root.querySelectorAll('[role="listbox"]');
    for (let i = 0; i < listboxes.length; i++) {
      const lb = listboxes[i];
      if (!lb) continue;
      const swatches = lb.querySelectorAll(".color-picker__swatch");
      for (let j = 0; j < swatches.length; j++) {
        const sw = swatches[j];
        if (!sw) continue;
        if (!sw.hasAttribute("aria-selected")) {
          findings.push({
            libId: LIB_ID,
            code: "WEB_COLOR_PICKER_SWATCH_MISSING_ARIA_SELECTED",
            category: "a11y",
            severity: "major",
            message: 'A .color-picker__swatch inside a [role="listbox"] is missing the aria-selected attribute; screen readers cannot announce the active swatch.',
            selector: ".color-picker__swatch",
            suggestedFix: "Add aria-selected (true on the active swatch, false otherwise)."
          });
        }
      }
    }
    const allSwatches = root.querySelectorAll(".color-picker__swatch[data-color]");
    for (let i = 0; i < allSwatches.length; i++) {
      const sw = allSwatches[i];
      if (!sw) continue;
      const container = sw.closest(".color-picker");
      if (!container) continue;
      const swColor = sw.getAttribute("data-color");
      const bgColor = container.getAttribute("data-color");
      if (!swColor || !bgColor) continue;
      const swRgb = hexToRgb2(swColor);
      const bgRgb = hexToRgb2(bgColor);
      if (!swRgb || !bgRgb) continue;
      const dr = Math.abs(swRgb[0] - bgRgb[0]);
      const dg = Math.abs(swRgb[1] - bgRgb[1]);
      const db = Math.abs(swRgb[2] - bgRgb[2]);
      if (dr + dg + db < 6) {
        findings.push({
          libId: LIB_ID,
          code: "STR_CONTRAST_INSUFFICIENT",
          category: "a11y",
          severity: "major",
          message: "A .color-picker__swatch is visually indistinguishable from its container background (combined channel delta < 6/765).",
          selector: ".color-picker__swatch",
          suggestedFix: "Add an outline or border on swatches; never rely on fill alone for visibility against the picker container.",
          extras: { swatch: swColor, container: bgColor }
        });
      }
    }
    const roots = root.querySelectorAll(".color-picker[data-color]");
    for (let i = 0; i < roots.length; i++) {
      const r = roots[i];
      if (!r) continue;
      const sourceHex = r.getAttribute("data-color");
      if (!sourceHex) continue;
      const parsed = hexToRgb2(sourceHex);
      if (!parsed) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_COLOR_PICKER_FORMAT_PARSE_ROUNDTRIP_BROKEN",
          category: "identity",
          severity: "major",
          message: `Root .color-picker data-color="${sourceHex}" failed to parse as a hex color.`,
          selector: ".color-picker",
          extras: { source: sourceHex }
        });
        continue;
      }
      const reformatted = `#${parsed.map((n) => n.toString(16).padStart(2, "0")).join("")}`;
      const sourceCanonical = normaliseHexLocal(sourceHex);
      if (sourceCanonical && sourceCanonical !== reformatted) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_COLOR_PICKER_FORMAT_PARSE_ROUNDTRIP_BROKEN",
          category: "identity",
          severity: "major",
          message: `Color value ${sourceHex} lost precision after parse+format roundtrip (became ${reformatted}).`,
          selector: ".color-picker",
          extras: { source: sourceHex, roundtripped: reformatted }
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_COLOR_PICKER_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeMin));
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-color-picker",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-color-picker",
  () => selfCheck
);

export { ColorAlphaSlider, ColorArea, ColorChannelInput, ColorEyeDropper, ColorHueSlider, ColorPicker, ColorPickerInput, ColorPickerPopover, ColorSwatchGrid, ColorTextInput, LIB_VERSION, RESPONSIVE_CONTRACT, WEB_COLOR_PICKER_LIB, clamp, deriveChannels, formatColor, hsvToRgb, normaliseHex, parseAnyColor, parseHslString, parseRgbString, rgbToHex, rgbToHsv, selfCheck, useColor, useColorPickerIds };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map